import { useSyncExternalStore } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";
import { dismissToast, getToasts, subscribeToasts, type ToastTone } from "../../lib/toast";

const TONES: Record<ToastTone, { box: string; icon?: typeof Info }> = {
  default: { box: "bg-ink text-white" },
  success: { box: "bg-tone-green-bg text-tone-green-fg", icon: CircleCheck },
  error: { box: "bg-tone-red-bg text-[#7A1A10]", icon: CircleAlert },
  warning: { box: "bg-tone-amber-bg text-[#5A3A00]", icon: TriangleAlert },
  info: { box: "bg-tone-blue-bg text-[#1F3A86]", icon: Info },
};

/** Renders the toasts from lib/toast. Mount once, at the app root. */
export default function Toaster() {
  const t = useT();
  const toasts = useSyncExternalStore(subscribeToasts, getToasts);

  return (
    <div
      aria-live="polite"
      // Top of the screen, just under the sticky header, where it's seen after a redirect.
      className="pointer-events-none fixed inset-x-4 top-18 z-90 flex flex-col items-center gap-2"
    >
      {toasts.map(({ id, tone, message }) => {
        const { box, icon: Icon } = TONES[tone];
        return (
          <div
            key={id}
            role={tone === "error" ? "alert" : "status"}
            className={cn(
              "pointer-events-auto flex max-w-110 items-start gap-2.5 rounded-md px-4 py-3 text-sm leading-normal shadow-[0_6px_24px_rgba(0,0,0,.16)]",
              box,
            )}
          >
            {Icon && <Icon size={18} className="mt-px shrink-0" aria-hidden />}
            <span className="flex-1">{message}</span>
            <button
              type="button"
              onClick={() => dismissToast(id)}
              aria-label={t.common.close}
              className="-mr-1 cursor-pointer border-0 bg-transparent p-0.5 text-current opacity-60 hover:opacity-100"
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
